import { Injectable } from '@angular/core';

import { environment } from '../../../environments/environment';
import {
  CookieConsentService,
  CookieConsentState,
} from './cookie-consent.service';
import { toAnalyticsPagePath } from '../util/analytics-page.util';

declare global {
  interface ClarityFn {
    (...args: unknown[]): void;
    q?: unknown[][];
  }

  interface Window {
    clarity?: ClarityFn;
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

@Injectable({
  providedIn: 'root',
})
export class AnalyticsLoaderService {
  private initialized = false;
  private trackingEnabled = false;
  private analyticsConsentGranted = false;
  private tagManagerId = '';
  private pendingPageViewUrl: string | undefined;

  constructor(private readonly cookieConsentService: CookieConsentService) {}

  init(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;

    // Staging and local builds must never report to the production container,
    // even if a browser runtime config is accidentally populated with its ID.
    if (!environment.production || !environment.analyticsEnabled) {
      return;
    }

    this.trackingEnabled = true;
    this.cookieConsentService.state$.subscribe((state) => {
      this.applyConsent(state);
    });
  }

  private applyConsent(state: CookieConsentState): void {
    if (!state.isReady) {
      return;
    }

    this.analyticsConsentGranted =
      state.hasSavedPreference && state.preferences.googleAnalytics;
    const advertisingConsentGranted =
      state.hasSavedPreference && state.preferences.googleAdsMarketing;

    if (!state.hasSavedPreference) {
      return;
    }

    if (this.analyticsConsentGranted || advertisingConsentGranted) {
      this.initGoogleTagManager(
        environment.googleTagManagerId,
        this.analyticsConsentGranted,
        advertisingConsentGranted
      );
    }

    if (state.preferences.microsoftClarity) {
      this.initClarity(environment.clarityProjectId);
    }

    if (this.analyticsConsentGranted && this.pendingPageViewUrl) {
      const url = this.pendingPageViewUrl;
      this.pendingPageViewUrl = undefined;
      this.trackPageView(url);
    } else if (!this.analyticsConsentGranted) {
      this.pendingPageViewUrl = undefined;
    }
  }

  /**
   * Records an Angular route page view for the GTM Custom Event trigger.
   */
  trackPageView(url: string): void {
    if (!this.initialized) {
      this.pendingPageViewUrl = url;
      return;
    }

    if (!this.trackingEnabled) {
      return;
    }

    if (!this.cookieConsentService.snapshot.hasSavedPreference) {
      this.pendingPageViewUrl = url;
      return;
    }

    if (!this.analyticsConsentGranted || !this.tagManagerId) {
      return;
    }

    const pagePath = toAnalyticsPagePath(url);
    const pageView = {
      page_location: new URL(pagePath, window.location.origin).href,
      page_path: pagePath,
      page_title: document.title,
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer?.push({
      event: 'citizex_page_view',
      citizex_analytics_consent: true,
      ...pageView,
    });
  }

  /** Records a completed product action for a GTM Custom Event trigger. */
  trackEvent(
    event: 'citizex_rsvp_completed',
    parameters: Record<string, string | number | boolean>
  ): void {
    if (
      !this.initialized ||
      !this.trackingEnabled ||
      !this.analyticsConsentGranted ||
      !this.tagManagerId
    ) {
      return;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer?.push({
      event,
      citizex_analytics_consent: true,
      ...parameters,
    });
  }

  private initGoogleTagManager(
    containerId: string,
    analyticsGranted: boolean,
    advertisingGranted: boolean
  ): void {
    this.tagManagerId = containerId;

    if (!containerId || this.hasScript(`gtm-${containerId}`)) {
      return;
    }

    const gtag = this.ensureGtag();

    // Queue the saved choice before GTM starts processing dataLayer entries.
    gtag('consent', 'default', {
      analytics_storage: analyticsGranted ? 'granted' : 'denied',
      ad_storage: advertisingGranted ? 'granted' : 'denied',
      ad_user_data: advertisingGranted ? 'granted' : 'denied',
      ad_personalization: advertisingGranted ? 'granted' : 'denied',
    });

    window.dataLayer?.push({
      event: 'gtm.js',
      'gtm.start': new Date().getTime(),
    });

    this.appendScript(
      `gtm-${containerId}`,
      `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(
        containerId
      )}`
    );

    window.dataLayer?.push({
      event: 'citizex_google_consent',
      citizex_analytics_consent: analyticsGranted,
      citizex_ads_consent: advertisingGranted,
    });
  }

  private ensureGtag(): NonNullable<Window['gtag']> {
    window.dataLayer = window.dataLayer || [];
    window.gtag =
      window.gtag ||
      (function () {
        // gtag.js recognizes commands queued as Arguments objects. A rest
        // parameter would create an Array and enter its legacy API path.
        // eslint-disable-next-line prefer-rest-params
        window.dataLayer?.push(arguments);
      } as (...args: unknown[]) => void);

    return window.gtag;
  }

  private initClarity(projectId: string): void {
    if (!projectId || this.hasScript(`clarity-${projectId}`)) {
      return;
    }

    if (typeof window.clarity !== 'function') {
      const clarity: ClarityFn = (...args: unknown[]) => {
        (clarity.q = clarity.q || []).push(args);
      };
      clarity.q = clarity.q || [];
      window.clarity = clarity;
    }

    this.appendScript(
      `clarity-${projectId}`,
      `https://www.clarity.ms/tag/${encodeURIComponent(projectId)}`
    );
  }

  private appendScript(id: string, src: string): void {
    const script = document.createElement('script');
    script.id = id;
    script.async = true;
    script.src = src;
    document.head.appendChild(script);
  }

  private hasScript(id: string): boolean {
    return !!document.getElementById(id);
  }
}
